// src/pages/Chart.tsx
// Desktop: marquee + 3-col grid + separate tab card + separate content card + 15 orders/side
// Mobile : matches TokenDetail.tsx (theme tokens, flat pill tabs, bottom nav), 5 tabs incl. TRUST

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  TrendingUp,
  TrendingDown,
  Flame,
  Loader2,
  Copy,
  Check,
  ShieldCheck,
  Home,
  LineChart,
  BarChart3,
  Zap,
  Plus,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useMultipleTokenPrices } from "@/hooks/use-dexscreener";
import { useTheme } from "@/contexts/ThemeContext";
import {
  getTokenTrades,
  getSolanaTokenHolders,
  Trade,
  Holder,
} from "@/lib/dexscreener";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface TokenInfo {
  symbol: string;
  name: string;
  price: number;
  change24h: number;
  high24h?: number;
  low24h?: number;
  volume24h?: number;
  volumeQuote24h?: number;
}

type TabKey = "chart" | "holders" | "trades" | "information" | "trust";

const TOKEN_CONFIG: Record<
  string,
  {
    address: string;
    chain: string;
    name: string;
    network?: string;
    deployedOn?: string;
    tags?: string[];
  }
> = {
  BTC: { address: "0x2260fac5e5542a773aa44fbcfedf7c193bc2c599", chain: "ethereum", name: "bitcoin", network: "Bitcoin", deployedOn: "2009-01-03", tags: ["Store of Value", "OG"] },
  WBTC: { address: "0x2260fac5e5542a773aa44fbcfedf7c193bc2c599", chain: "ethereum", name: "bitcoin", network: "Ethereum", deployedOn: "2018-10-26", tags: ["Wrapped", "DeFi"] },
  ETH: { address: "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2", chain: "ethereum", name: "ethereum", network: "Ethereum", deployedOn: "2015-07-30", tags: ["Layer 1", "Smart Contracts"] },
  WETH: { address: "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2", chain: "ethereum", name: "ethereum", network: "Ethereum", deployedOn: "2015-07-30", tags: ["Wrapped", "DeFi"] },
  BNB: { address: "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c", chain: "bsc", name: "bnb", network: "BNB Chain", deployedOn: "2017-07-25", tags: ["Layer 1", "Exchange Token"] },
  WBNB: { address: "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c", chain: "bsc", name: "bnb", network: "BNB Chain", deployedOn: "2017-07-25", tags: ["Wrapped", "DeFi"] },
  SOL: { address: "So11111111111111111111111111111111111111112", chain: "solana", name: "solana", network: "Solana", deployedOn: "2020-03-16", tags: ["Layer 1", "High Performance"] },
  MATIC: { address: "0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270", chain: "polygon", name: "polygon", network: "Polygon", deployedOn: "2017-10-11", tags: ["Layer 2", "Scaling"] },
  UNI: { address: "0x1f9840a85d5af5bf1d1762f925bdaddc4201f984", chain: "ethereum", name: "uniswap", network: "Ethereum", deployedOn: "2020-09-16", tags: ["DeFi", "DEX"] },
  LINK: { address: "0x514910771af9ca656af840dff83e8264ecf986ca", chain: "ethereum", name: "chainlink", network: "Ethereum", deployedOn: "2017-09-19", tags: ["Oracle", "DeFi"] },
  CAKE: { address: "0x0e09fabb73bd3ade0a17ecc321fd13a19e81ce82", chain: "bsc", name: "pancakeswap", network: "BNB Chain", deployedOn: "2020-09-20", tags: ["DeFi", "DEX"] },
 SHIB: { address: "0x95aD61b0a150d79219dCF64E1E6Cc01f0B64C4cE" , chain: "ethereum" , name: "shiba inu" , network: "Ethereum" , deplouedOn: "2018-06-5", tags: ["Defi"] },
  FIXERCOIN: { address: "H4qKn8FMFha8jJuj8xMryMqRhH3h7GjLuxw7TVixpump", chain: "solana", name: "fixercoin", network: "Solana", deployedOn: "2024-01-01", tags: ["Meme", "Community"] },
  PINGX: { address: "7KS4DgKHmgSWYC4uGnSozLUon2bDEj6WKhRNSosmpump", chain: "solana", name: "pingx", network: "Solana", deployedOn: "2024-01-15", tags: ["Meme", "Gaming"] },
  LOCKER: { address: "EN1nYrW6375zMPUkpkGyGSEXW8WmAqYu4yhf6xnGpump", chain: "solana", name: "locker", network: "Solana", deployedOn: "2024-02-01", tags: ["DeFi", "Staking"] },
  FXM: { address: "7Fnx57ztmhdpL1uAGmUY1ziwPG2UDKmG6poB4ibjpump", chain: "solana", name: "fxm", network: "Solana", deployedOn: "2024-02-15", tags: ["Exchange", "Utility"] },
  USDT: { address: "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB", chain: "solana", name: "tether", network: "Solana", deployedOn: "2020-01-01", tags: ["Stablecoin"] },
  USDC: { address: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v", chain: "solana", name: "usd coin", network: "Solana", deployedOn: "2020-01-01", tags: ["Stablecoin"] },
  JUP: { address: "JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN", chain: "solana", name: "jupiter", network: "Solana", deployedOn: "2021-10-01", tags: ["DeFi", "DEX Aggregator"] },
  BONK: { address: "DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263", chain: "solana", name: "bonk", network: "Solana", deployedOn: "2022-12-25", tags: ["Meme", "Community"] },
  PYTH: { address: "Gnt27xtC473ZT2Mw5u8wZ68Z3gULkSTb5DuxJy7eJotD", chain: "solana", name: "pyth", network: "Solana", deployedOn: "2023-11-15", tags: ["Oracle", "DeFi"] },
  ORCA: { address: "orcaEKTdK7LKz57vaAYr9QeNsVEPfiu6QeMU1kektZE", chain: "solana", name: "orca", network: "Solana", deployedOn: "2021-09-15", tags: ["DEX", "DeFi"] },
  RAY: { address: "4k3Dyjzvzp8eMZWUXbBCjEvwSkkk59S5iCNLY3QrkX6R", chain: "solana", name: "raydium", network: "Solana", deployedOn: "2021-02-15", tags: ["DEX", "DeFi"] },
  JTO: { address: "jtojtomepa8beP8AuQc6eXt5FriJwfFMwQx2v2f9mCL", chain: "solana", name: "jito", network: "Solana", deployedOn: "2023-12-07", tags: ["DeFi", "Liquid Staking"] },
};

const generateOrders = (basePrice: number, isBuy: boolean, count: number = 15) => {
  const orders = [];
  const isSmallToken = basePrice < 0.001;
  for (let i = 0; i < count; i++) {
    const multiplier = isBuy ? 1 - (i + 1) * 0.0005 : 1 + (i + 1) * 0.0005;
    const price = basePrice * multiplier;
    let amount: number;
    if (isSmallToken) amount = parseFloat((Math.random() * 490000 + 10000).toFixed(2));
    else amount = parseFloat((Math.random() * 99 + 1).toFixed(4));
    const total = price * amount;
    orders.push({ price, amount, total });
  }
  return orders;
};

const formatCompactAmount = (num: number): string => {
  if (num === null || num === undefined || isNaN(num)) return "0";
  const abs = Math.abs(num);
  if (abs === 0) return "0";
  if (abs >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
  if (abs >= 1) return num.toFixed(2);
  if (abs >= 0.01) return num.toFixed(4);
  if (abs >= 0.0001) return num.toFixed(6);
  return num.toExponential(2);
};

const shortenAddress = (addr: string, head = 6, tail = 4): string => {
  if (!addr) return "";
  if (addr.length <= head + tail + 2) return addr;
  return `${addr.slice(0, head)}…${addr.slice(-tail)}`;
};

const formatVolume = (volume: number): string => {
  if (!volume || volume === 0) return "--";
  if (volume >= 1e9) return `$${(volume / 1e9).toFixed(2)}B`;
  if (volume >= 1e6) return `$${(volume / 1e6).toFixed(2)}M`;
  if (volume >= 1e3) return `$${(volume / 1e3).toFixed(2)}K`;
  return `$${volume.toFixed(2)}`;
};

const formatHolders = (holders: number): string => {
  if (!holders || holders === 0) return "--";
  if (holders >= 1e6) return `${(holders / 1e6).toFixed(2)}M`;
  if (holders >= 1e3) return `${(holders / 1e3).toFixed(1)}K`;
  return holders.toString();
};

const getBirdeyeWidgetUrl = (tokenSymbol: string, isDarkMode: boolean): string | null => {
  const config = TOKEN_CONFIG[tokenSymbol.toUpperCase()];
  if (!config) return null;
  const { address, chain } = config;

  const theme = isDarkMode ? "dark" : "light";
  const backgroundColor = isDarkMode ? "%23000000" : "%23FFFFFF";
  const textColor = isDarkMode ? "%23FFFFFF" : "%23111A28";
  const borderColor = isDarkMode ? "%233F3F46" : "%23E5E7EB";
  const upColor = "%2322C55E";
  const downColor = "%23EF4444";

  return `https://embed.birdeye.so/tv-widget/${address}?chain=${chain}&viewMode=pair&chartInterval=1D&chartType=Candle&chartTimezone=Asia%2FKarachi&chartLeftToolbar=hide&theme=${theme}&backgroundColor=${backgroundColor}&textColor=${textColor}&borderColor=${borderColor}&chartOverrides=mainSeriesProperties.candleStyle.downColor=${downColor}&chartOverrides=mainSeriesProperties.candleStyle.borderDownColor=${downColor}&chartOverrides=mainSeriesProperties.candleStyle.wickDownColor=${downColor}&chartOverrides=mainSeriesProperties.candleStyle.upColor=${upColor}&chartOverrides=mainSeriesProperties.candleStyle.borderUpColor=${upColor}&chartOverrides=mainSeriesProperties.candleStyle.wickUpColor=${upColor}`;
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between items-center py-1.5 border-b border-gray-200 dark:border-zinc-800">
    <span className="text-[11px] text-gray-500 dark:text-gray-400 uppercase">{label}</span>
    <span className="text-[11px] font-medium text-gray-900 dark:text-white text-right break-all ml-2 uppercase">{value}</span>
  </div>
);

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-border/50">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-sm font-medium text-foreground break-all text-right ml-2">{value}</span>
  </div>
);

const TradeDetailsDialog: React.FC<{
  trade: Trade | null;
  symbol: string;
  chain: string;
  onClose: () => void;
}> = ({ trade, symbol, chain, onClose }) => {
  const [copiedField, setCopiedField] = useState<"maker" | "tx" | null>(null);
  if (!trade) return null;

  const handleCopy = (text: string, field: "maker" | "tx") => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    });
  };

  return (
    <Dialog open={!!trade} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[92%] max-w-sm bg-card border-border text-foreground rounded-xl">
        <DialogHeader>
          <DialogTitle className="text-sm uppercase tracking-wider flex items-center justify-between">
            <span>TRADE DETAILS</span>
            <span
              className={`text-[10px] px-2 py-0.5 rounded font-bold ${
                trade.type === "buy"
                  ? "bg-green-500/20 text-green-400"
                  : "bg-red-500/20 text-red-400"
              }`}
            >
              {trade.type.toUpperCase()}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <Row label="TOKEN" value={`${symbol} (${chain.toUpperCase()})`} />
          <Row label="PRICE" value={`$${trade.price.toFixed(8)}`} />
          <Row label="AMOUNT" value={`${formatCompactAmount(trade.amount)} ${symbol}`} />
          <Row label="TOTAL (USD)" value={`$${trade.total.toFixed(2)}`} />
          <Row label="TIME" value={trade.timestamp} />

          <div className="flex justify-between items-center py-2 border-b border-border/50 gap-2">
            <span className="text-xs text-muted-foreground uppercase shrink-0">MAKER</span>
            <div className="flex items-center gap-1 min-w-0">
              <span className="text-[10px] font-mono text-foreground truncate">
                {shortenAddress(trade.maker, 8, 6)}
              </span>
              <button
                onClick={() => handleCopy(trade.maker, "maker")}
                className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
              >
                {copiedField === "maker" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              </button>
            </div>
          </div>

          <div className="flex justify-between items-center py-2 gap-2">
            <span className="text-xs text-muted-foreground uppercase shrink-0">TX</span>
            <div className="flex items-center gap-1 min-w-0">
              <span className="text-[10px] font-mono text-foreground truncate">
                {shortenAddress(trade.id, 10, 8)}
              </span>
              <button
                onClick={() => handleCopy(trade.id, "tx")}
                className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
              >
                {copiedField === "tx" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              </button>
            </div>
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full mt-3 font-bold py-2.5 rounded-md bg-yellow-500 hover:bg-yellow-400 text-black text-xs uppercase tracking-wider"
        >
          CLOSE
        </button>
      </DialogContent>
    </Dialog>
  );
};

interface TrustFactor {
  label: string;
  impact: number;
  ok: boolean;
}

const computeTrust = (
  tokenSymbol: string,
  tokenAddress: string | null,
  chain: string,
  holders: Holder[],
  volume24h: number
): { score: number; label: string; factors: TrustFactor[] } => {
  const factors: TrustFactor[] = [];
  let score = 0;

  if (tokenAddress && tokenAddress.length >= 32) {
    score += 40;
    factors.push({ label: "VALID CONTRACT ADDRESS", impact: +40, ok: true });
  } else {
    factors.push({ label: "MISSING OR INVALID CONTRACT ADDRESS", impact: -40, ok: false });
  }

  const isKnown = !!TOKEN_CONFIG[tokenSymbol.toUpperCase()];
  if (isKnown) {
    score += 30;
    factors.push({ label: "TOKEN IN VERIFIED LIST", impact: +30, ok: true });
  } else {
    factors.push({ label: "TOKEN NOT IN VERIFIED LIST", impact: 0, ok: false });
  }

  if (holders.length > 0) {
    const topPct = holders[0]?.percentage || 0;
    if (topPct > 30) {
      score -= 20;
      factors.push({ label: `TOP HOLDER ${topPct.toFixed(1)}% (>30%)`, impact: -20, ok: false });
    } else {
      score += 10;
      factors.push({ label: `TOP HOLDER ${topPct.toFixed(1)}% (HEALTHY)`, impact: +10, ok: true });
    }
    if (holders.length >= 10) {
      score += 20;
      factors.push({ label: "BROAD HOLDER BASE (≥10)", impact: +20, ok: true });
    }
  } else {
    factors.push({
      label: chain === "solana" ? "HOLDERS DATA UNAVAILABLE" : "EVM HOLDERS NOT SUPPORTED",
      impact: 0,
      ok: false,
    });
  }

  if (volume24h > 10000) {
    score += 10;
    factors.push({ label: "24H VOLUME > $10K", impact: +10, ok: true });
  } else {
    factors.push({ label: "LOW 24H VOLUME", impact: 0, ok: false });
  }

  score = Math.max(0, Math.min(100, score));
  const label = score >= 70 ? "HIGH" : score >= 40 ? "MEDIUM" : "LOW";
  return { score, label, factors };
};

// ============ Main Component ============
export default function Chart() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isDarkMode } = useTheme();
  const tokenSymbol = searchParams.get("token") || "BTC";

  const tokenConfig = TOKEN_CONFIG[tokenSymbol.toUpperCase()];
  const tokenAddress = tokenConfig?.address || null;
  const chain = tokenConfig?.chain || "solana";

  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true
  );
  const [extraInfo, setExtraInfo] = useState<{
    high: number;
    low: number;
    volume: number;
    volumeQuote: number;
  } | null>(null);

  const [buyOrders, setBuyOrders] = useState<any[]>([]);
  const [sellOrders, setSellOrders] = useState<any[]>([]);

  const [activeTab, setActiveTab] = useState<TabKey>("chart");
  const [trades, setTrades] = useState<Trade[]>([]);
  const [holdersList, setHoldersList] = useState<Holder[]>([]);
  const [isLoadingTrades, setIsLoadingTrades] = useState(false);
  const [isLoadingHolders, setIsLoadingHolders] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState<Trade | null>(null);

  const isLoadingTradesRef = useRef(false);
  const isLoadingHoldersRef = useRef(false);

  const [priceFlash, setPriceFlash] = useState<"up" | "down" | null>(null);
  const prevPriceRef = useRef<number>(0);

  const birdeyeUrl = getBirdeyeWidgetUrl(tokenSymbol, isDarkMode);

  const allTokenAddresses = useMemo(
    () => Object.values(TOKEN_CONFIG).map((c) => c.address),
    []
  );

  const { prices: dexPrices, loading: pricesLoading } = useMultipleTokenPrices(
    allTokenAddresses,
    { refetchInterval: 30000 }
  );

  const livePrices = useMemo(() => {
    const map: Record<string, { price: number; change: number }> = {};
    Object.entries(TOKEN_CONFIG).forEach(([symbol, config]) => {
      const priceData = dexPrices[config.address];
      if (priceData && priceData.priceUsd > 0) {
        map[symbol] = {
          price: priceData.priceUsd,
          change: priceData.priceChange24h || 0,
        };
      }
    });
    return map;
  }, [dexPrices]);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Extra info from DexScreener
  useEffect(() => {
    if (!tokenAddress) return;
    const fetchExtra = async () => {
      try {
        const res = await fetch(
          `https://api.dexscreener.com/latest/dex/tokens/${tokenAddress}`
        );
        const data = await res.json();
        if (data.pairs && data.pairs.length > 0) {
          const relevantPairs = data.pairs.filter((p: any) => {
            const q = p.quoteToken?.symbol?.toUpperCase();
            return q === "USDT" || q === "USDC" || q === "USD";
          });
          const pair = relevantPairs.length ? relevantPairs[0] : data.pairs[0];
          setExtraInfo({
            high: parseFloat(pair.priceHigh24h || pair.high24h || 0),
            low: parseFloat(pair.priceLow24h || pair.low24h || 0),
            volume: parseFloat(pair.volume?.h24 || 0),
            volumeQuote: parseFloat(pair.volumeQuote?.h24 || 0),
          });
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchExtra();
  }, [tokenAddress]);

  // Main token info
  useEffect(() => {
    const sym = tokenSymbol.toUpperCase();
    const live = livePrices[sym];
    if (live && live.price > 0) {
      setTokenInfo({
        symbol: sym,
        name: sym,
        price: live.price,
        change24h: live.change,
        high24h: extraInfo?.high,
        low24h: extraInfo?.low,
        volume24h: extraInfo?.volume,
        volumeQuote24h: extraInfo?.volumeQuote,
      });
      setLoading(false);
    } else {
      setTokenInfo(null);
      setLoading(true);
    }
  }, [tokenSymbol, livePrices, extraInfo]);

  // Order book generator (15 rows per side, refresh every 3s)
  useEffect(() => {
    if (tokenInfo && tokenInfo.price > 0) {
      const orderCount = 15;
      setBuyOrders(generateOrders(tokenInfo.price, true, orderCount));
      setSellOrders(generateOrders(tokenInfo.price, false, orderCount));
      const interval = setInterval(() => {
        if (tokenInfo && tokenInfo.price > 0) {
          setBuyOrders(generateOrders(tokenInfo.price, true, orderCount));
          setSellOrders(generateOrders(tokenInfo.price, false, orderCount));
        }
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [tokenInfo?.price]);

  // Trades fetcher — TokenDetail-style
  const fetchTradesData = useCallback(async (mint: string, chainId: string) => {
    if (!mint || isLoadingTradesRef.current) return;
    isLoadingTradesRef.current = true;
    setIsLoadingTrades(true);
    try {
      const result = await getTokenTrades(mint, chainId);
      setTrades(result.trades || []);
      if (result.volume24h > 0) {
        setExtraInfo((prev) =>
          prev ? { ...prev, volumeQuote: result.volume24h } : prev
        );
      }
    } catch (err) {
      console.error("Error fetching trades:", err);
      setTrades([]);
    } finally {
      isLoadingTradesRef.current = false;
      setIsLoadingTrades(false);
    }
  }, []);

  // Holders fetcher — TokenDetail-style
  const fetchHoldersData = useCallback(async (mint: string, chainId: string) => {
    if (!mint || isLoadingHoldersRef.current) return;
    isLoadingHoldersRef.current = true;
    setIsLoadingHolders(true);
    try {
      let data: Holder[] = [];
      if (chainId === "solana") {
        data = await getSolanaTokenHolders(mint);
      }
      setHoldersList(data.length > 0 ? data.slice(0, 20) : []);
    } catch (err) {
      console.error("Error fetching holders:", err);
      setHoldersList([]);
    } finally {
      isLoadingHoldersRef.current = false;
      setIsLoadingHolders(false);
    }
  }, []);

  // Tab-driven fetching — mirrors TokenDetail (refetch on tab open)
  useEffect(() => {
    if (!tokenAddress) return;
    if (activeTab === "trades") fetchTradesData(tokenAddress, chain);
    if (activeTab === "holders" || activeTab === "trust") fetchHoldersData(tokenAddress, chain);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tokenAddress, chain, tokenSymbol]);

  // Reset on token change
  useEffect(() => {
    setActiveTab("chart");
    setTrades([]);
    setHoldersList([]);
    setSelectedTrade(null);
  }, [tokenSymbol]);

  // Price flash
  useEffect(() => {
    if (tokenInfo?.price && prevPriceRef.current > 0) {
      if (tokenInfo.price > prevPriceRef.current) {
        setPriceFlash("up");
        setTimeout(() => setPriceFlash(null), 300);
      } else if (tokenInfo.price < prevPriceRef.current) {
        setPriceFlash("down");
        setTimeout(() => setPriceFlash(null), 300);
      }
    }
    prevPriceRef.current = tokenInfo?.price || 0;
  }, [tokenInfo?.price]);

  const handleTrade = () => {
    localStorage.setItem("swap_selected_token", tokenSymbol.toUpperCase());
    navigate("/swap");
  };
  const handleConvert = () => {
    localStorage.setItem("swap_selected_token", tokenSymbol.toUpperCase());
    navigate("/convert");
  };
  const handleTransfer = () => {
    localStorage.setItem("transfer_selected_token", tokenSymbol.toUpperCase());
    navigate("/transfer");
  };

  const formatPrice = (price: number) => {
    if (!price || price === 0) return "---";
    if (price < 0.000001) return `$${price.toFixed(10)}`;
    if (price < 0.00001) return `$${price.toFixed(8)}`;
    if (price < 0.001) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(2)}`;
  };

  const formatNumber = (num: number, decimals = 2) => {
    if (!num) return "---";
    if (num >= 1e9) return `$${(num / 1e9).toFixed(decimals)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(decimals)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(decimals)}K`;
    return `$${num.toFixed(decimals)}`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "UNKNOWN";
    return new Date(dateString)
      .toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
      .toUpperCase();
  };

  const DesktopActionButtons = () => (
    <div className="flex gap-2">
      <button
        onClick={handleTrade}
        className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold h-9 text-xs uppercase rounded"
      >
        DEX TRADE
      </button>
      <button
        onClick={handleConvert}
        className="flex-1 border border-yellow-500 text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-500/10 h-9 text-xs uppercase rounded"
      >
        MARKET
      </button>
      <button
        onClick={handleTransfer}
        className="flex-1 border border-yellow-500 text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-500/10 h-9 text-xs uppercase rounded"
      >
        TRANSFER
      </button>
    </div>
  );

  const AnimatedOrderRow = ({ order, type }: { order: any; type: "buy" | "sell" }) => {
    const [animate, setAnimate] = useState(false);
    useEffect(() => {
      setAnimate(true);
      const timer = setTimeout(() => setAnimate(false), 500);
      return () => clearTimeout(timer);
    }, [order.price, order.total]);
    return (
      <div
        className={`flex justify-between text-[10px] transition-all duration-300 ${
          animate
            ? type === "buy"
              ? "translate-y-[-2px] text-emerald-400"
              : "translate-y-[2px] text-red-400"
            : ""
        }`}
      >
        <span className={type === "buy" ? "text-emerald-500" : "text-red-500"}>
          {formatPrice(order.price)}
        </span>
        <span className="text-gray-500 dark:text-gray-400">
          {formatPrice(order.total)}
        </span>
      </div>
    );
  };

  // Desktop tab bar — 5 tabs, no underline
  const TextTabBar = () => {
    const tabs: Array<{ key: TabKey; label: string }> = [
      { key: "chart", label: "CHART" },
      { key: "holders", label: "HOLDERS" },
      { key: "trades", label: "TRADES" },
      { key: "information", label: "INFO" },
      { key: "trust", label: "TRUST" },
    ];
    return (
      <div className="flex items-center justify-around py-1">
        {tabs.map((t) => {
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-2 py-2 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                isActive
                  ? "text-gray-900 dark:text-white"
                  : "text-gray-500 dark:text-gray-400"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    );
  };

  // Shared tab content for holders / trades / trust / information
  // Styled for BOTH desktop and mobile — uses theme tokens where relevant
  const TabContent = () => {
    if (activeTab === "holders") {
      return (
        <div className="p-4 h-full overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-foreground uppercase">
              TOP 20 HOLDERS
            </h3>
            <span className="text-xs text-muted-foreground uppercase">
              TOTAL: {formatHolders(holdersList.length)}
            </span>
          </div>
          {isLoadingHolders ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : holdersList.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-muted-foreground">
              <p className="text-xs text-center px-6 uppercase">
                {chain === "solana"
                  ? "HOLDERS DATA COULD NOT BE LOADED FOR THIS TOKEN."
                  : "HOLDER DATA IS NOT AVAILABLE FOR EVM TOKENS."}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground uppercase tracking-wider pb-2 border-b border-border/50">
                <span className="w-8">#</span>
                <span className="flex-1 px-2">ADDRESS</span>
                <span className="w-24 text-right">BALANCE</span>
                <span className="w-16 text-right">%</span>
              </div>
              {holdersList.map((holder) => (
                <div
                  key={holder.rank}
                  className="flex items-center justify-between py-1.5 text-sm border-b border-border/30"
                >
                  <span className="w-8 text-muted-foreground">{holder.rank}</span>
                  <span className="flex-1 px-2 font-mono text-xs truncate">
                    {shortenAddress(holder.address, 6, 4)}
                  </span>
                  <span className="w-24 text-right font-medium">
                    {formatCompactAmount(holder.balance)}
                  </span>
                  <span className="w-16 text-right text-muted-foreground">
                    {holder.percentage.toFixed(2)}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (activeTab === "trades") {
      return (
        <div className="p-4 h-full overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-foreground uppercase">
              LAST 8 TRADES
            </h3>
            <span className="text-xs text-muted-foreground uppercase">
              24H VOL: {formatVolume(extraInfo?.volumeQuote || 0)}
            </span>
          </div>
          {isLoadingTrades ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : trades.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-muted-foreground">
              <p className="text-xs text-center px-6 uppercase">
                TRADE HISTORY NOT AVAILABLE FOR {tokenSymbol.toUpperCase()} ON {chain.toUpperCase()}.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground uppercase tracking-wider pb-2 border-b border-border/50">
                <span className="w-14">TYPE</span>
                <span className="flex-1 text-right">PRICE</span>
                <span className="w-16 text-right">AMOUNT</span>
                <span className="w-20 text-right">TOTAL</span>
                <span className="w-16 text-right">TIME</span>
              </div>
              {trades.map((trade) => (
                <button
                  key={trade.id}
                  onClick={() => setSelectedTrade(trade)}
                  className="w-full flex items-center justify-between py-1.5 text-sm border-b border-border/30 hover:bg-secondary/50 transition-colors rounded-sm px-1 -mx-1"
                >
                  <span
                    className={`w-14 font-medium ${
                      trade.type === "buy" ? "text-green-500" : "text-red-500"
                    }`}
                  >
                    {trade.type.toUpperCase()}
                  </span>
                  <span className="flex-1 text-right">${trade.price.toFixed(6)}</span>
                  <span className="w-16 text-right">
                    {formatCompactAmount(trade.amount)}
                  </span>
                  <span className="w-20 text-right">${trade.total.toFixed(2)}</span>
                  <span className="w-16 text-right text-xs text-muted-foreground">
                    {trade.timestamp}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (activeTab === "trust") {
      const trust = computeTrust(
        tokenSymbol,
        tokenAddress,
        chain,
        holdersList,
        extraInfo?.volumeQuote || 0
      );
      const color =
        trust.label === "HIGH"
          ? "text-emerald-500"
          : trust.label === "MEDIUM"
          ? "text-yellow-500"
          : "text-red-500";
      const bg =
        trust.label === "HIGH"
          ? "bg-emerald-500"
          : trust.label === "MEDIUM"
          ? "bg-yellow-500"
          : "bg-red-500";
      return (
        <div className="p-4 h-full overflow-y-auto">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className={`w-4 h-4 ${color}`} />
            <h3 className="text-sm font-medium text-foreground uppercase">
              TOKEN TRUST LEVEL
            </h3>
          </div>

          <div className="mb-4">
            <div className="flex items-end justify-between mb-2">
              <span className={`text-3xl font-bold ${color}`}>{trust.score}/100</span>
              <span className={`text-xs font-semibold uppercase ${color}`}>
                {trust.label}
              </span>
            </div>
            <div className="w-full h-2 rounded-full bg-secondary overflow-hidden">
              <div
                className={`h-full ${bg} transition-all duration-500`}
                style={{ width: `${trust.score}%` }}
              />
            </div>
          </div>

          <div className="space-y-1">
            {trust.factors.map((f, i) => (
              <div
                key={i}
                className="flex items-center justify-between py-2 border-b border-border/30"
              >
                <span className="text-[10px] uppercase text-foreground">
                  {f.label}
                </span>
                <span
                  className={`text-[10px] font-semibold uppercase ${
                    f.ok ? "text-emerald-500" : "text-muted-foreground"
                  }`}
                >
                  {f.impact > 0 ? `+${f.impact}` : f.impact < 0 ? f.impact : "—"}
                </span>
              </div>
            ))}
          </div>

          <p className="mt-4 text-[10px] text-muted-foreground uppercase leading-relaxed">
            TRUST SCORE IS A HEURISTIC BASED ON CONTRACT PRESENCE, KNOWN-LIST
            MEMBERSHIP, HOLDER CONCENTRATION AND VOLUME. NOT FINANCIAL ADVICE.
          </p>
        </div>
      );
    }

    // information — no 24h high / low / tags
    return (
      <div className="flex-1 overflow-y-auto px-4 py-4 bg-background">
        <div className="w-full max-w-sm mx-auto space-y-3">
          <InfoRow label="SYMBOL" value={tokenSymbol.toUpperCase()} />
          <InfoRow label="NAME" value={(tokenConfig?.name || tokenSymbol).toUpperCase()} />
          <InfoRow label="NETWORK" value={(tokenConfig?.network || chain.toUpperCase()).toUpperCase()} />
          <InfoRow label="CHAIN" value={chain.toUpperCase()} />
          <InfoRow label="DEPLOYED ON" value={formatDate(tokenConfig?.deployedOn)} />
          <InfoRow
            label="CONTRACT"
            value={tokenAddress ? shortenAddress(tokenAddress, 8, 6) : "N/A"}
          />
          <InfoRow label="PRICE" value={formatPrice(tokenInfo?.price || 0)} />
          <InfoRow
            label="24H CHANGE"
            value={`${(tokenInfo?.change24h || 0) >= 0 ? "+" : ""}${(tokenInfo?.change24h || 0).toFixed(2)}%`}
          />
        </div>
      </div>
    );
  };

  // Loading / error guards
  if ((loading || pricesLoading) && !tokenInfo && !birdeyeUrl) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-foreground mx-auto mb-4" />
          <p className="text-muted-foreground text-sm uppercase">LOADING TOKEN DATA...</p>
        </div>
      </div>
    );
  }

  if (!tokenInfo && !birdeyeUrl && !pricesLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground text-sm uppercase">
            PRICE DATA NOT AVAILABLE FOR {tokenSymbol.toUpperCase()}
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="text-foreground hover:text-foreground/80 mt-4 text-sm"
          >
            BACK TO DASHBOARD
          </button>
        </div>
      </div>
    );
  }

  const isPositive = tokenInfo?.change24h && tokenInfo.change24h >= 0;
  const displayPrice = tokenInfo?.price ?? 0;
  const displayChange = tokenInfo?.change24h ?? 0;


  // ================= Desktop View =================
  if (isDesktop) {
    const Marquee = () => {
      const priceEntries = Object.entries(livePrices);
      const loopEntries = [...priceEntries, ...priceEntries];
      return (
        <div className="overflow-hidden whitespace-nowrap border-b border-gray-200 dark:border-zinc-800 py-2 mb-2">
          <div className="inline-block animate-marquee-desktop">
            {loopEntries.map(([sym, data], idx) => {
              const tokenName = TOKEN_CONFIG[sym]?.name || sym.toLowerCase();
              return (
                <div key={idx} className="inline-flex items-center mx-4 text-[10px] gap-2">
                  <span className="text-gray-500 dark:text-gray-400 uppercase">{sym}</span>
                  <span className="text-gray-500 dark:text-gray-400 uppercase">{tokenName}</span>
                  <span className="text-gray-900 dark:text-white font-semibold">
                    {formatPrice(data.price)}
                  </span>
                  <span className={`font-semibold ${data.change >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {data.change >= 0 ? "+" : ""}
                    {data.change.toFixed(2)}%
                  </span>
                </div>
              );
            })}
          </div>
          <style>{`
            @keyframes marquee-desktop { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
            .animate-marquee-desktop { animation: marquee-desktop 30s linear infinite; }
          `}</style>
        </div>
      );
    };

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black text-gray-900 dark:text-white px-4 pt-1 pb-4">
        <Marquee />

        <div className="grid grid-cols-[320px_minmax(0,1fr)_320px] gap-4 items-stretch min-h-[calc(100vh-6rem)]">
          {/* Left column — price card + Sell Orders */}
          <div className="flex flex-col gap-4 min-h-0">
            <div className="bg-white dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800 rounded-xl p-4 space-y-3 shadow-sm dark:shadow-none">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white uppercase">
                    {tokenSymbol}/USDT
                  </h2>
                  <div className="text-2xl font-bold mt-1 text-gray-900 dark:text-white">
                    {formatPrice(displayPrice)}
                  </div>
                  <div className={`flex items-center gap-1 text-sm ${isPositive ? "text-green-600" : "text-red-600"}`}>
                    {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {isPositive ? "+" : ""}
                    {displayChange.toFixed(2)}%
                  </div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 px-2 py-1 rounded-md text-xs font-semibold flex items-center gap-1 border border-yellow-200 dark:border-yellow-500/20 uppercase">
                  <Flame className="w-3 h-3" /> HOT
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="text-gray-500 dark:text-gray-400 uppercase">24H HIGH</div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {formatPrice(tokenInfo?.high24h || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400 uppercase">24H LOW</div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {formatPrice(tokenInfo?.low24h || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400 uppercase">24H VOL({tokenSymbol})</div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {formatNumber(tokenInfo?.volume24h || 0, 0)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400 uppercase">24H VOL(USDT)</div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {formatNumber(tokenInfo?.volumeQuote24h || 0, 0)}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800 rounded-xl flex flex-col flex-1 min-h-0 shadow-sm dark:shadow-none">
              <div className="p-3 border-b border-gray-200 dark:border-zinc-800">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-red-500">
                  SELL ORDERS
                </h3>
              </div>
              <div className="flex-1 p-3 overflow-auto">
                <div className="flex justify-between text-[10px] text-gray-500 dark:text-gray-400 uppercase mb-2">
                  <span>PRICE</span>
                  <span>TOTAL</span>
                </div>
                <div className="space-y-1">
                  {sellOrders.map((order, i) => (
                    <AnimatedOrderRow key={i} order={order} type="sell" />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Center column — separate tab card + separate content card */}
          <div className="flex flex-col gap-4 min-h-0">
            <div className="bg-white dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800 rounded-xl shadow-sm dark:shadow-none">
              <TextTabBar />
            </div>

            <div className="bg-white dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800 rounded-xl overflow-hidden shadow-sm dark:shadow-none flex-1 min-h-0">
              {activeTab === "chart" ? (
                birdeyeUrl ? (
                  <iframe
                    src={birdeyeUrl}
                    className="w-full h-full"
                    style={{
                      border: "none",
                      background: isDarkMode ? "#000000" : "#FFFFFF",
                    }}
                    allowFullScreen
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500 uppercase">
                    <p>CHART NOT AVAILABLE FOR {tokenSymbol.toUpperCase()}</p>
                  </div>
                )
              ) : (
                <div className="h-full overflow-y-auto">
                  <TabContent />
                </div>
              )}
            </div>
          </div>

          {/* Right column — Actions + Buy Orders */}
          <div className="flex flex-col gap-4 min-h-0">
            <div className="bg-white dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800 rounded-xl p-4 shadow-sm dark:shadow-none">
              <DesktopActionButtons />
            </div>
            <div className="bg-white dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800 rounded-xl flex flex-col flex-1 min-h-0 shadow-sm dark:shadow-none">
              <div className="p-3 border-b border-gray-200 dark:border-zinc-800">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-emerald-500">
                  BUY ORDERS
                </h3>
              </div>
              <div className="flex-1 p-3 overflow-auto">
                <div className="flex justify-between text-[10px] text-gray-500 dark:text-gray-400 uppercase mb-2">
                  <span>PRICE</span>
                  <span>TOTAL</span>
                </div>
                <div className="space-y-1">
                  {buyOrders.map((order, i) => (
                    <AnimatedOrderRow key={i} order={order} type="buy" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <TradeDetailsDialog
          trade={selectedTrade}
          symbol={tokenSymbol.toUpperCase()}
          chain={chain}
          onClose={() => setSelectedTrade(null)}
        />
      </div>
    );
  }

  // ================= Mobile View =================
  // Matches TokenDetail.tsx: theme tokens, flat pill tabs (no underline),
  // inline header (SYMBOL + price flash + change + chain), bottom nav.
  return (
    <div className="h-screen bg-background text-foreground flex flex-col">
      {/* Header — matches TokenDetail */}
      {/* Header — matches TokenDetail */}
      <div className="flex items-center justify-between px-4 pt-2 pb-0 bg-background">
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold text-foreground uppercase">
            {tokenSymbol.toUpperCase()}
          </span>
          <span
            className={`text-sm font-semibold transition-colors duration-150 ${
              priceFlash === "up"
                ? "text-green-500"
                : priceFlash === "down"
                ? "text-red-500"
                : "text-foreground"
            }`}
          >
            {formatPrice(displayPrice)}
          </span>
          <span
            className={`text-sm font-bold ${
              isPositive ? "text-green-500" : "text-red-500"
            }`}
          >
            {isPositive ? "+" : ""}
            {displayChange.toFixed(2)}%
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground ml-1">
            {chain.toUpperCase()}
          </span>
        </div>
        <div className="w-8"></div>
      </div>

      {/* Tab buttons — plain text, no yellow, no underline */}
            {/* Tab buttons — plain text, left-aligned within each button */}
      <div className="px-4 pt-1 pb-2 bg-background border-b border-border">
        <div className="flex items-center gap-3">
          {(
            [
              { key: "chart", label: "CHART" },
              { key: "holders", label: "HOLDERS" },
              { key: "trades", label: "TRADES" },
              { key: "information", label: "INFO" },
              { key: "trust", label: "TRUST" },
            ] as Array<{ key: TabKey; label: string }>
          ).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`py-1.5 text-left text-[11px] font-medium uppercase transition-colors ${
                activeTab === key
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {/* Content — chart OR tab content, same layout as TokenDetail */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {activeTab === "chart" ? (
          <div
            className={`flex-1 border border-border overflow-hidden ${
              isDarkMode ? "bg-black" : "bg-white"
            }`}
          >
            {birdeyeUrl ? (
              <iframe
                src={birdeyeUrl}
                className="w-full h-full"
                style={{
                  border: "none",
                  background: isDarkMode ? "#000000" : "#FFFFFF",
                }}
                allowFullScreen
              />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                <div className="text-center px-6">
                  <p className="text-sm">
                    CHART NOT AVAILABLE FOR {chain.toUpperCase()}
                  </p>
                  <p className="text-xs mt-2 text-muted-foreground/70">
                    CHART DATA IS PROVIDED BY BIRDEYE (SOLANA, ETHEREUM, BSC, POLYGON ONLY).
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-4 py-4 bg-background">
            <TabContent />
          </div>
        )}
      </div>

      <TradeDetailsDialog
        trade={selectedTrade}
        symbol={tokenSymbol.toUpperCase()}
        chain={chain}
        onClose={() => setSelectedTrade(null)}
      />

    

    </div>
  );
}
